<template>
    <div :class="['col-span-12', addClass]">
        <label v-if="label" class="mb-2 block text-sm font-semibold uppercase text-principal">{{ label }}</label>

        <div class="flex min-h-12 items-center gap-2 border border-lineas bg-white px-3">
            <div class="min-w-0 flex-1">
                <p v-if="multiple && selectedIds.length" class="truncate text-sm font-semibold">
                    {{ selectedIds.length }} elementi selezionati
                </p>
                <p v-else-if="selectedElement" class="truncate text-sm font-semibold" :title="elementLabel(selectedElement)">
                    {{ elementLabel(selectedElement) }}
                </p>
                <p v-else-if="selectedIds.length" class="truncate text-sm font-semibold">
                    Elemento #{{ selectedIds[0] }} selezionato
                </p>
                <p v-else class="text-sm text-[#716C65]">Nessun elemento selezionato</p>
            </div>
            <button v-if="selectedIds.length && !disabled && !readonly" type="button" title="Rimuovi selezione"
                aria-label="Rimuovi selezione" class="h-11 w-11 text-xl" @click="setValue(multiple ? [] : null)">×</button>
            <button type="button" class="h-11 bg-[#312D32] px-4 text-sm font-semibold text-white"
                :disabled="disabled || readonly" @click="openMap">Apri mappa</button>
        </div>

        <div v-if="errorMessage" class="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p class="text-sm font-semibold text-[#A83B32]">{{ errorMessage }}</p>
            <button type="button" class="min-h-10 border border-[#A83B32] px-3 text-xs font-semibold uppercase text-[#A83B32]"
                @click="retryLoad">Riprova</button>
        </div>

        <Teleport to="body">
            <section v-if="mapOpen" class="fixed inset-0 z-[7000] flex flex-col bg-[#F1F0EC]" role="dialog"
                aria-modal="true" aria-label="Seleziona elemento topografico">
                <header class="flex min-h-16 items-center justify-between gap-3 border-b border-[#312D32]/15 bg-white px-3 md:px-5">
                    <div class="min-w-0">
                        <h2 class="truncate text-base font-semibold">Elemento topografico III</h2>
                        <p class="text-xs text-[#716C65]">Seleziona il sito, poi usa la mappa o la posizione del dispositivo</p>
                    </div>
                    <button type="button" class="h-12 w-12 text-2xl" aria-label="Chiudi mappa" @click="closeMap">×</button>
                </header>

                <div class="grid min-h-0 flex-1 grid-rows-[minmax(12rem,35vh)_minmax(0,1fr)] lg:grid-cols-[minmax(18rem,34%)_1fr] lg:grid-rows-1">
                    <div class="relative min-h-0 min-w-0 bg-[#D7D4CD] lg:order-2">
                        <VMap ref="mapRef" class="h-full w-full" :customLayers="mapLayers" :leafletConfig="leafletConfig"
                            :center="mapCenter" :fitBounds="false" :defaultFitPolygonBounds="false"
                            :preferCanvas="true" :polygons="mapPolygons" @click="handleMapClick" />

                        <div v-if="loading"
                            class="absolute inset-0 z-[1100] flex items-center justify-center bg-white/75 px-5 text-center text-sm font-semibold">
                            Caricamento elementi del sito…
                        </div>

                        <div v-if="useDeviceLocation && locating"
                            class="absolute left-3 top-3 z-[1000] bg-white/95 px-3 py-2 text-xs font-semibold shadow">
                            Localizzazione…
                        </div>

                        <div v-if="useDeviceLocation"
                            class="absolute bottom-3 right-3 z-[1000]">
                            <button type="button" data-test="recenter-location" title="Trova la mia posizione"
                                aria-label="Trova la mia posizione"
                                class="flex h-12 w-12 items-center justify-center bg-[#312D32] text-white shadow"
                                @click="findUserPosition">
                                <Icon name="location-marker" :size="20" aria-hidden="true" />
                            </button>
                        </div>
                    </div>

                    <aside class="flex min-h-0 min-w-0 flex-col border-r border-[#312D32]/15 bg-[#F1F0EC] lg:order-1">
                        <div class="max-h-[65%] shrink-0 space-y-3 overflow-y-auto border-b border-[#312D32]/15 p-3">
                            <div class="grid grid-cols-2 gap-1" role="group" aria-label="Filtra per sito">
                                <button v-for="site in sites" :key="site.id" type="button"
                                    class="min-h-11 border px-3 text-xs font-semibold uppercase"
                                    :class="String(selectedSiteId) === String(site.id)
                                        ? 'border-[#312D32] bg-[#312D32] text-white'
                                        : 'border-[#312D32]/25 bg-white'"
                                    @click="selectSite(site)">
                                    {{ site.name }}
                                </button>
                            </div>
                            <div class="grid gap-2 md:grid-cols-3 lg:grid-cols-1">
                                <label class="min-w-0 text-xs font-semibold">
                                    Zona
                                    <select v-model="selectedZoneId" data-test="zone-filter"
                                        :disabled="!selectedSiteId || loading"
                                        class="mt-1 min-h-11 w-full border border-[#312D32]/25 bg-white px-3 text-sm disabled:opacity-50">
                                        <option value="">Tutte le zone</option>
                                        <option v-for="option in zoneOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
                                    </select>
                                </label>
                                <label class="min-w-0 text-xs font-semibold">
                                    Elemento topografico I
                                    <select v-model="selectedElement1Id" data-test="element1-filter"
                                        :disabled="!selectedZoneId || loading"
                                        class="mt-1 min-h-11 w-full border border-[#312D32]/25 bg-white px-3 text-sm disabled:opacity-50">
                                        <option value="">Tutti gli elementi I</option>
                                        <option v-for="option in element1Options" :key="option.id" :value="option.id">{{ option.name }}</option>
                                    </select>
                                </label>
                                <label class="min-w-0 text-xs font-semibold">
                                    Elemento topografico II (Monumento)
                                    <select v-model="selectedMonumentId" data-test="monument-filter"
                                        :disabled="!selectedElement1Id || loading"
                                        class="mt-1 min-h-11 w-full border border-[#312D32]/25 bg-white px-3 text-sm disabled:opacity-50">
                                        <option value="">Tutti gli elementi II</option>
                                        <option v-for="option in monumentOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
                                    </select>
                                </label>
                            </div>
                            <input v-model.trim="searchTerm" type="search" placeholder="Cerca elemento o percorso…"
                                :disabled="!selectedSiteId"
                                class="h-12 w-full border border-[#312D32]/25 bg-white px-3 text-sm focus:border-[#8D3F35] focus:outline-none disabled:opacity-50" />
                            <label class="flex min-h-14 cursor-pointer items-center gap-3 border px-3 py-2 transition-colors"
                                :class="useDeviceLocation
                                    ? 'border-[#8D3F35]/45 bg-[#F6ECE9]'
                                    : 'border-[#312D32]/20 bg-white hover:border-[#8D3F35]/45'">
                                <span class="flex h-9 w-9 shrink-0 items-center justify-center bg-[#F1F0EC] text-[#8D3F35]"
                                    aria-hidden="true">
                                    <Icon name="location-marker" :size="18" />
                                </span>
                                <span class="min-w-0 flex-1 text-sm font-semibold">Usa la posizione del dispositivo</span>
                                <input v-model="useDeviceLocation" data-test="use-device-location" type="checkbox"
                                    class="peer sr-only" />
                                <span class="relative h-6 w-11 shrink-0 rounded-full bg-[#B8B1A6] transition-colors
                                    after:absolute after:left-[3px] after:top-[3px] after:h-[18px] after:w-[18px]
                                    after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:content-['']
                                    peer-checked:bg-[#8D3F35] peer-checked:after:translate-x-5
                                    peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2
                                    peer-focus-visible:outline-[#8D3F35]" aria-hidden="true"></span>
                            </label>
                            <p v-if="!selectedSiteId" class="text-xs font-semibold text-[#716C65]">Seleziona Paestum o Velia.</p>
                            <p v-if="offline" class="text-xs font-semibold text-[#896A25]">Catalogo offline salvato sul dispositivo</p>
                            <p v-if="useDeviceLocation && gpsWarning" class="text-xs font-semibold text-[#A83B32]">{{ gpsWarning }}</p>
                            <div v-if="selectedSiteId && !loading" class="flex items-center justify-between gap-3 text-xs text-[#716C65]">
                                <span>{{ mapPolygons.length }} geometrie visibili</span>
                            </div>
                        </div>

                        <div v-if="loading" class="p-5 text-sm">Caricamento elementi…</div>
                        <div v-else ref="listRef" class="min-h-0 flex-1 overflow-y-auto">
                            <button v-for="elementItem in filteredElements" :key="elementItem.id" type="button"
                                :data-element-id="elementItem.id"
                                :aria-pressed="String(candidate?.id) === String(elementItem.id)"
                                class="flex min-h-14 w-full items-center justify-between gap-3 border-b border-[#312D32]/10 px-3 py-2 text-left"
                                :class="String(candidate?.id) === String(elementItem.id) || selectedIds.includes(String(elementItem.id))
                                    ? 'bg-[#E7D8D3]'
                                    : 'bg-transparent'"
                                @click="chooseCandidate(elementItem)">
                                <span class="min-w-0">
                                    <span class="block truncate text-sm font-semibold">{{ elementItem.name }}</span>
                                    <span class="mt-1 block truncate text-xs text-[#716C65]" :title="elementLabel(elementItem)">{{ elementLabel(elementItem) }}</span>
                                </span>
                                <span class="shrink-0 text-[11px] uppercase text-[#716C65]">{{ elementTypeLabel(elementItem) }}</span>
                            </button>
                            <p v-if="!filteredElements.length" class="p-5 text-sm text-[#716C65]">Nessun elemento trovato.</p>
                        </div>

                        <div v-if="candidate" class="border-t border-[#312D32]/15 bg-white p-3">
                            <p class="text-xs font-semibold uppercase text-[#716C65]">Elemento da confermare</p>
                            <p class="mt-1 text-sm font-semibold">{{ elementLabel(candidate) }}</p>
                            <div class="mt-3 grid grid-cols-2 gap-2">
                                <button type="button" class="h-12 border border-[#312D32]/25" @click="clearCandidate">Annulla</button>
                                <button type="button" class="h-12 bg-[#3E6158] font-semibold text-white" @click="confirmCandidate">
                                    {{ multiple && candidateSelected ? 'Rimuovi' : 'Conferma' }}
                                </button>
                            </div>
                        </div>
                        <div v-else-if="multiple && selectedIds.length" class="border-t border-[#312D32]/15 bg-white p-3">
                            <button type="button" class="h-12 w-full bg-[#3E6158] font-semibold text-white" @click="closeMap">
                                Conferma {{ selectedIds.length }} elementi
                            </button>
                        </div>
                    </aside>
                </div>
            </section>
        </Teleport>
    </div>
</template>

<script>
import { defineElement } from '@vueform/vueform';
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import Icon from '@/Components/Icon.vue';
import { generateMapLayers, getMapLayers, LEAFLET_CONFIG } from '@/utils/map';
import { getMapCatalog, registeredMapLayers, registeredMapView } from '@/utils/mapLayers';
import { HERA_MAINTENANCE_URL } from '@/modules/map-assets/legacy';
import { distanceToLineMeters, getTouchRadiusMeters, hasLineGeometry } from '@/utils/mapGeometry';
import { getVMapClickedPolygonData } from '@/utils/vMapEvents';
import { getTenantContext } from '@/composables/useTenant';
import {
    getMaintenanceReferenceData,
    putMaintenanceReferenceData,
} from '@/modules/hera-maintenance/offline/maintenanceDb';
import { calculatedTopographicFields } from '@/modules/hera-maintenance/schemaFieldExtension';

const SITES_REFERENCE_KEY = 'topographic-sites-v6';
const SITE_REFERENCE_KEY_PREFIX = 'topographic-elements-v6';
const LOCATION_OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 };
const MAP_TOUCH_TARGET_SIZE_PIXELS = 52;

export default defineElement({
    name: 'HeraTopographicMapElement',
    components: { Icon },
    props: {
        modelValue: { type: [Array, Object, String, Number], default: null },
        name: { type: String, required: true },
        label: { type: String, default: '' },
        endpoint: { type: String, default: '/api/hera/maintenance/reference-data' },
        multiple: { type: Boolean, default: false },
        siteField: { type: String, default: 'parkId' },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        addClass: { type: [String, Array, Object], default: '' },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit, element }) {
        const tenant = getTenantContext();
        const scope = { tenantId: tenant.id, userId: tenant.user?.id };
        const locationPreferenceKey = [
            'hera-maintenance',
            'topographic-map',
            'use-device-location',
            tenant.id ?? 'tenant',
            tenant.user?.id ?? 'user',
        ].join(':');
        const elements = shallowRef([]);
        const sites = shallowRef([]);
        const loading = ref(false);
        const offline = ref(false);
        const mapOpen = ref(false);
        const searchTerm = ref('');
        const candidate = ref(null);
        // Inside Vueform the value lives in element.model; modelValue only feeds standalone usage.
        const formModelValue = computed(() => (element?.model ? element.model.value : props.modelValue));
        const selectedIds = ref(normalizeSelectionIds(formModelValue.value));
        const selectedSiteOverride = ref(null);
        const selectedZoneId = ref('');
        const selectedElement1Id = ref('');
        const selectedMonumentId = ref('');
        const locating = ref(false);
        const gpsWarning = ref('');
        const userPosition = ref(null);
        const useDeviceLocation = ref(readLocationPreference());
        const errorMessage = ref('');
        const loadedSiteId = ref(null);
        const mapCenter = ref(registeredMapView('hera.maintenance').center ?? JSON.parse(tenant.settings['map.center'] ?? '[0,0]'));
        const mapRef = ref(null);
        const listRef = ref(null);
        let siteLoadSequence = 0;
        let locationWatchId = null;
        let locationMarker = null;
        let mapSessionSequence = 0;
        const leafletConfig = computed(() => ({ ...LEAFLET_CONFIG, ...registeredMapView('hera.maintenance') }));
        const backgroundMapLayers = getMapLayers('map.hera_background_tiles_url');
        const archaeologicalMapLayers = getMapLayers('map.hera_archaeological_tiles_url');
        const newArchaeologicalMapLayers = getMapCatalog() ? [] : generateMapLayers(HERA_MAINTENANCE_URL, { maxNativeZoom: 22 });
        const existingTileLayers = [
            ...(backgroundMapLayers[0]?.layers ?? []),
            ...archaeologicalMapLayers.flatMap(group => group.layers ?? []),
        ];
        const highestTileZIndex = [...existingTileLayers, ...newArchaeologicalMapLayers.flatMap(group => group.layers ?? [])]
            .reduce((highest, layer) => Math.max(highest, Number(layer.config?.zIndex) || 0), 0);
        const topTileLayers = newArchaeologicalMapLayers.flatMap(group => group.layers ?? []).map(layer => ({
            ...layer,
            config: { ...layer.config, zIndex: highestTileZIndex + 1 },
        }));
        const primaryMapLayer = backgroundMapLayers[0]
            ?? archaeologicalMapLayers[0]
            ?? newArchaeologicalMapLayers[0];
        const legacyMapLayers = primaryMapLayer ? [
            {
                ...primaryMapLayer,
                layers: [...existingTileLayers, ...topTileLayers],
            },
            ...backgroundMapLayers.slice(1),
        ] : [];
        const mapLayers = computed(() => getMapCatalog() ? registeredMapLayers('hera.maintenance') : legacyMapLayers);

        const selectedSiteId = computed(() => {
            const formValue = readFormField(props.siteField);
            return normalizeSelectionIds(formValue)[0] ?? selectedSiteOverride.value ?? null;
        });
        const selectedElements = computed(() => elements.value.filter(
            item => selectedIds.value.includes(String(item.id)),
        ));
        const selectedElement = computed(() => selectedElements.value[0] ?? null);
        const candidateSelected = computed(() => candidate.value
            ? selectedIds.value.includes(String(candidate.value.id))
            : false);
        const visibleElements = computed(() => selectedSiteId.value
            ? elements.value.filter(item => String(item.siteId) === String(selectedSiteId.value))
            : []);
        const hierarchies = computed(() => visibleElements.value.flatMap(item => item.hierarchies ?? []));
        const zoneOptions = computed(() => hierarchyOptions(hierarchies.value, 'zoneId', 'zoneName'));
        const element1Options = computed(() => hierarchyOptions(
            hierarchies.value.filter(path => String(path.zoneId) === String(selectedZoneId.value)),
            'topographicElement1Id', 'topographicElement1Name',
        ));
        const monumentOptions = computed(() => hierarchyOptions(
            hierarchies.value.filter(path => String(path.zoneId) === String(selectedZoneId.value)
                && String(path.topographicElement1Id) === String(selectedElement1Id.value)),
            'monumentId', 'monumentName',
        ));
        const matchingElements = computed(() => {
            const matchingHierarchy = visibleElements.value.filter(item => !selectedZoneId.value
                || (item.hierarchies ?? []).some(matchesHierarchy));
            const term = searchTerm.value.toLowerCase();
            return term
                ? matchingHierarchy.filter(item => [elementLabel(item), elementTypeLabel(item),
                    ...(item.hierarchies ?? []).flatMap(path => [path.zoneName, path.topographicElement1Name, path.monumentName])]
                    .join(' ')
                    .toLowerCase()
                    .includes(term))
                : matchingHierarchy;
        });
        const matchingPolygonElements = computed(() => matchingElements.value.filter(item => item.polygon));
        const filteredElements = computed(() => {
            const matching = matchingElements.value;
            if (!candidate.value || matching.some(item => String(item.id) === String(candidate.value.id))) {
                return matching;
            }

            return [candidate.value, ...matching];
        });
        const displayedMapElements = computed(() => {
            const displayed = matchingPolygonElements.value;
            const requiredIds = new Set([
                ...selectedIds.value,
                candidate.value?.id,
            ].filter(value => value !== null && typeof value !== 'undefined').map(String));
            const additional = visibleElements.value.filter(item => requiredIds.has(String(item.id))
                && !displayed.some(displayedItem => String(displayedItem.id) === String(item.id)));

            return [...displayed, ...additional];
        });
        const displayedLineElements = computed(() => displayedMapElements.value.filter(
            item => hasLineGeometry(item.polygon),
        ));
        const displayedLineElementIds = computed(() => new Set(
            displayedLineElements.value.map(item => String(item.id)),
        ));
        const mapPolygons = computed(() => displayedMapElements.value
            .filter(item => item.polygon)
            .map(item => {
                const active = String(item.id) === String(candidate.value?.id)
                    || selectedIds.value.includes(String(item.id));
                const isLine = displayedLineElementIds.value.has(String(item.id));
                return {
                    polygon: item.polygon,
                    sheet: { id: item.id },
                    uuid: `hera-maintenance-${item.id}-${active ? 'active' : 'idle'}`,
                    tooltip: `Tipo: ${elementTypeLabel(item)} · ${elementLabel(item)}`,
                    style: {
                        color: active ? '#8D3F35' : '#655D53',
                        fillColor: active ? '#C86B5D' : '#B8B1A6',
                        fillOpacity: isLine ? 0 : (active ? 0.62 : 0.2),
                        weight: isLine ? (active ? 8 : 6) : (active ? 3 : 1),
                        ...(isLine ? { lineCap: 'round', lineJoin: 'round' } : {}),
                    },
                    hoverStyle: {
                        color: '#8D3F35',
                        fillColor: '#C86B5D',
                        fillOpacity: isLine ? 0 : 0.5,
                        weight: isLine ? 8 : 3,
                        ...(isLine ? { lineCap: 'round', lineJoin: 'round' } : {}),
                    },
                };
            }));

        function hierarchyOptions(paths, idField, nameField) {
            const options = new Map();
            paths.forEach(path => {
                if (path[idField] === null || path[idField] === undefined) return;
                const id = String(path[idField]);
                options.set(id, { id, name: path[nameField] || `#${id}` });
            });
            return [...options.values()].sort((left, right) => left.name.localeCompare(right.name, 'it', { numeric: true }));
        }

        function elementLabel(item) {
            if (selectedZoneId.value) {
                const path = (item?.hierarchies ?? []).find(matchesHierarchy);
                if (path) {
                    return [item.siteName ?? item.path?.[0], path.zoneName, path.topographicElement1Name, path.monumentName, item.name]
                        .filter(Boolean)
                        .join(' › ');
                }
            }
            return [...(item?.path ?? []), item?.path?.at(-1) === item?.name ? null : item?.name]
                .filter(Boolean)
                .join(' › ');
        }

        function matchesHierarchy(path) {
            return String(path.zoneId) === String(selectedZoneId.value)
                && (!selectedElement1Id.value || String(path.topographicElement1Id) === String(selectedElement1Id.value))
                && (!selectedMonumentId.value || String(path.monumentId) === String(selectedMonumentId.value));
        }

        function elementTypeLabel(item) {
            return item?.typeName || item?.tipologia || 'Non specificato';
        }

        function readLocationPreference() {
            if (typeof window === 'undefined') return false;
            try {
                return window.localStorage.getItem(locationPreferenceKey) === 'true';
            } catch {
                return false;
            }
        }

        function persistLocationPreference(value) {
            if (typeof window === 'undefined') return;
            try {
                window.localStorage.setItem(locationPreferenceKey, value ? 'true' : 'false');
            } catch {
                // The preference remains valid for the current session.
            }
        }

        function normalizeSelectionIds(value) {
            const values = Array.isArray(value) ? value : [value];

            return values
                .flatMap(item => Array.isArray(item) ? item : [item])
                .map(item => typeof item === 'object' && item !== null ? (item.id ?? item.value) : item)
                .filter(item => item !== null && item !== undefined && item !== '')
                .map(String)
                .filter((item, index, source) => source.indexOf(item) === index);
        }

        function readFormField(fieldName) {
            const form = element?.form$?.value;
            const model = form?.model?.value ?? form?.model;
            if (model && typeof model === 'object' && !Array.isArray(model)) return model[fieldName];
            const internalData = form?.internalData?.value ?? form?.internalData;
            return internalData && typeof internalData === 'object' && !Array.isArray(internalData)
                ? internalData[fieldName]
                : null;
        }

        function setValue(value) {
            const ids = normalizeSelectionIds(value);
            const normalized = props.multiple
                ? ids.map(Number).filter(Number.isFinite)
                : (ids.length ? Number(ids[0]) : null);
            selectedIds.value = props.multiple
                ? normalized.map(String)
                : (normalized === null ? [] : [String(normalized)]);
            syncCalculatedTopographicFields();
            if (element?.model) element.model.value = normalized;
            emit('update:modelValue', normalized);
            emit('change', normalized);
        }

        function syncCalculatedTopographicFields() {
            const selected = elements.value.filter(item => selectedIds.value.includes(String(item.id)));
            const form = element?.form$?.value;

            Object.entries(calculatedTopographicFields(selected)).forEach(([field, value]) => {
                form?.updateModel?.(field, value);
            });
        }

        async function getReferencePayload(cacheKey, params = {}) {
            const cached = await getMaintenanceReferenceData(scope, cacheKey).catch(() => null);
            try {
                const response = await axios.get(props.endpoint, {
                    params: { ...params, schema_version: 6 },
                    headers: cached?.payload?._etag ? { 'If-None-Match': cached.payload._etag } : {},
                    validateStatus: status => status === 200 || status === 304,
                });
                const payload = response.status === 304 ? cached?.payload : {
                    ...response.data,
                    _etag: response.headers?.etag,
                };
                if (!payload) throw new Error('Catalogo topografico non disponibile');
                if (response.status === 200) {
                    await putMaintenanceReferenceData(scope, cacheKey, payload).catch(() => undefined);
                }

                return { payload, fromCache: false };
            } catch (error) {
                if (cached?.payload) return { payload: cached.payload, fromCache: true };
                throw error;
            }
        }

        async function loadSites(force = false) {
            if (!force && sites.value.length) return;
            loading.value = true;
            errorMessage.value = '';
            try {
                const result = await getReferencePayload(SITES_REFERENCE_KEY);
                sites.value = result.payload.sites ?? [];
                offline.value = result.fromCache;
            } catch {
                errorMessage.value = 'Impossibile caricare i siti. Verifica la connessione e riprova.';
            } finally {
                loading.value = false;
            }
        }

        async function loadSiteElements(siteId, force = false) {
            const normalizedSiteId = String(siteId ?? '');
            if (!normalizedSiteId || (!force && loadedSiteId.value === normalizedSiteId)) return;
            const sequence = ++siteLoadSequence;
            loading.value = true;
            errorMessage.value = '';
            try {
                const result = await getReferencePayload(
                    `${SITE_REFERENCE_KEY_PREFIX}:${normalizedSiteId}`,
                    { park_id: normalizedSiteId },
                );
                if (sequence !== siteLoadSequence) return;
                elements.value = result.payload.elements ?? [];
                sites.value = result.payload.sites ?? sites.value;
                loadedSiteId.value = normalizedSiteId;
                offline.value = result.fromCache;
                syncCalculatedTopographicFields();
                await nextTick();
                focusVisibleElements();
            } catch {
                if (sequence !== siteLoadSequence) return;
                errorMessage.value = 'Impossibile caricare gli elementi del sito. Verifica la connessione e riprova.';
            } finally {
                if (sequence === siteLoadSequence) loading.value = false;
            }
        }

        async function openMap() {
            const session = ++mapSessionSequence;
            mapOpen.value = true;
            if (!sites.value.length) await loadSites();
            if (!mapOpen.value || session !== mapSessionSequence) return;
            await nextTick();
            if (!mapOpen.value || session !== mapSessionSequence) return;
            mapRef.value?.map?.invalidateSize?.();
            if (useDeviceLocation.value) startLocationTracking();
            if (selectedSiteId.value) await loadSiteElements(selectedSiteId.value);
            focusVisibleElements();
        }

        function closeMap() {
            mapSessionSequence += 1;
            stopLocationTracking();
            clearLocationLayers();
            siteLoadSequence += 1;
            loading.value = false;
            userPosition.value = null;
            gpsWarning.value = '';
            mapOpen.value = false;
            clearCandidate();
        }

        function chooseCandidate(item, scrollToItem = false) {
            candidate.value = item;
            if (scrollToItem) {
                nextTick(() => {
                    const row = listRef.value?.querySelector(`[data-element-id="${item.id}"]`);
                    row?.scrollIntoView?.({ block: 'nearest' });
                });
            }
        }

        function clearCandidate() {
            candidate.value = null;
        }

        function confirmCandidate() {
            if (!candidate.value) return;
            if (props.multiple) {
                const candidateId = String(candidate.value.id);
                const nextIds = candidateSelected.value
                    ? selectedIds.value.filter(id => id !== candidateId)
                    : [...selectedIds.value, candidateId];
                setValue(nextIds);
                clearCandidate();
                return;
            }

            setValue(candidate.value.id);
            closeMap();
        }

        function selectSite(site) {
            selectedSiteOverride.value = String(site.id);
            const form = element?.form$?.value;
            form?.updateModel?.(props.siteField, Number(site.id));
        }

        async function retryLoad() {
            if (!sites.value.length) {
                await loadSites(true);
                return;
            }
            if (selectedSiteId.value) await loadSiteElements(selectedSiteId.value, true);
        }

        function focusVisibleElements() {
            const coordinates = [];
            const collect = value => {
                if (!Array.isArray(value)) return;
                if (value.length >= 2 && value.every(Number.isFinite)) {
                    coordinates.push(value);
                    return;
                }
                value.forEach(collect);
            };
            displayedMapElements.value.forEach(item => collect(item.polygon?.coordinates));
            if (!coordinates.length) return;
            const longitudes = coordinates.map(point => point[0]);
            const latitudes = coordinates.map(point => point[1]);
            mapRef.value?.map?.fitBounds?.([
                [Math.min(...latitudes), Math.min(...longitudes)],
                [Math.max(...latitudes), Math.max(...longitudes)],
            ], { padding: [24, 24] });
        }

        function focusUserPosition() {
            const map = mapRef.value?.map;
            if (!map || !userPosition.value) return;
            const { latitude, longitude } = userPosition.value;
            const currentZoom = Number(map.getZoom?.());
            map.setView?.(
                [latitude, longitude],
                Number.isFinite(currentZoom) ? Math.max(currentZoom, 18) : 18,
            );
        }

        function findUserPosition() {
            if (userPosition.value) {
                focusUserPosition();
                return;
            }
            stopLocationTracking();
            startLocationTracking();
        }

        function findTappedLine(payload) {
            const latLng = payload?.event?.latlng ?? payload?.event?.lngLat;
            const latitude = Number(Array.isArray(latLng) ? latLng[1] : latLng?.lat);
            const longitude = Number(Array.isArray(latLng) ? latLng[0] : latLng?.lng);
            const zoom = Number(mapRef.value?.map?.getZoom?.() ?? leafletConfig.value.minZoom);
            const touchRadius = getTouchRadiusMeters(latitude, zoom, MAP_TOUCH_TARGET_SIZE_PIXELS);
            if (!Number.isFinite(longitude) || touchRadius === null) return null;

            let nearest = null;
            displayedLineElements.value.forEach(item => {
                const distance = distanceToLineMeters(item.polygon, [longitude, latitude]);
                if (distance === null || distance > touchRadius) return;
                if (!nearest || distance < nearest.distance) nearest = { distance, item };
            });

            return nearest?.item ?? null;
        }

        function handleMapClick(payload) {
            const tappedLine = findTappedLine(payload);
            if (tappedLine) {
                chooseCandidate(tappedLine, true);
                return;
            }

            const raw = getVMapClickedPolygonData(payload, { strategy: 'last' });
            if (!raw?.id) return;
            const item = elements.value.find(entry => String(entry.id) === String(raw.id));
            if (item) chooseCandidate(item, true);
        }

        function updateLocationLayers() {
            const map = mapRef.value?.map;
            const leaflet = typeof window !== 'undefined' ? window.L : null;
            if (!useDeviceLocation.value || !map || !leaflet || !userPosition.value) return;
            const latLng = [userPosition.value.latitude, userPosition.value.longitude];

            if (!locationMarker) {
                locationMarker = leaflet.circleMarker(latLng, {
                    radius: 7,
                    color: '#FFFFFF',
                    fillColor: '#2D6F9F',
                    fillOpacity: 1,
                    weight: 3,
                    interactive: false,
                }).addTo(map);
            } else {
                locationMarker.setLatLng?.(latLng);
            }
        }

        function clearLocationLayers() {
            locationMarker?.remove?.();
            locationMarker = null;
        }

        async function handleLocation(position) {
            if (!mapOpen.value || !useDeviceLocation.value) return;
            const latitude = Number(position.coords?.latitude);
            const longitude = Number(position.coords?.longitude);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
            const accuracy = Number(position.coords?.accuracy);
            locating.value = false;
            userPosition.value = { latitude, longitude };
            gpsWarning.value = '';

            const form = element?.form$?.value;
            form?.updateModel?.('latitude', Number(latitude.toFixed(7)));
            form?.updateModel?.('longitude', Number(longitude.toFixed(7)));
            if (Number.isFinite(accuracy)) {
                form?.updateModel?.('locationAccuracy', Number(Math.max(0, accuracy).toFixed(2)));
            }
            form?.updateModel?.('locationCapturedAt', new Date(position.timestamp ?? Date.now()).toISOString());

            await nextTick();
            updateLocationLayers();
        }

        function handleLocationError(error) {
            locating.value = false;
            gpsWarning.value = error?.code === 1
                ? 'Permesso di localizzazione negato. Seleziona manualmente.'
                : 'Posizione non disponibile. Riprova o seleziona manualmente.';
        }

        function startLocationTracking() {
            if (!useDeviceLocation.value || locationWatchId !== null) return;
            if (typeof navigator === 'undefined' || !navigator.geolocation?.watchPosition) {
                gpsWarning.value = 'Localizzazione non disponibile su questo dispositivo.';
                return;
            }
            locating.value = true;
            gpsWarning.value = '';
            locationWatchId = navigator.geolocation.watchPosition(
                position => { handleLocation(position); },
                handleLocationError,
                LOCATION_OPTIONS,
            );
        }

        function stopLocationTracking() {
            if (locationWatchId !== null && typeof navigator !== 'undefined') {
                navigator.geolocation?.clearWatch?.(locationWatchId);
            }
            locationWatchId = null;
            locating.value = false;
        }

        function disableLocationMode() {
            stopLocationTracking();
            clearLocationLayers();
            userPosition.value = null;
            gpsWarning.value = '';
        }

        watch(formModelValue, value => {
            selectedIds.value = normalizeSelectionIds(value);
        });
        // Resolve names for selections made elsewhere (prefill/edit) without waiting for the map to open.
        watch([selectedIds, selectedSiteId], ([ids, siteId]) => {
            if (!ids.length || !siteId) return;
            if (selectedElements.value.length === ids.length) return;
            loadSiteElements(siteId).catch(() => { });
        }, { immediate: true });
        watch(selectedSiteId, (value, previous) => {
            searchTerm.value = '';
            selectedZoneId.value = '';
            selectedElement1Id.value = '';
            selectedMonumentId.value = '';
            clearCandidate();
            if (String(value ?? '') !== String(previous ?? '')) {
                elements.value = [];
                loadedSiteId.value = null;
                if (previous !== null && previous !== undefined) setValue(props.multiple ? [] : null);
                if (mapOpen.value && value) loadSiteElements(value);
            }
        });
        watch(selectedZoneId, () => {
            selectedElement1Id.value = '';
            selectedMonumentId.value = '';
            clearCandidate();
        });
        watch(selectedElement1Id, () => {
            selectedMonumentId.value = '';
            clearCandidate();
        });
        watch(selectedMonumentId, clearCandidate);
        watch(useDeviceLocation, enabled => {
            persistLocationPreference(enabled);
            if (!mapOpen.value) return;
            if (enabled) {
                startLocationTracking();
                return;
            }
            disableLocationMode();
        });
        onMounted(loadSites);
        onBeforeUnmount(() => {
            stopLocationTracking();
            clearLocationLayers();
        });

        return {
            candidate, candidateSelected, clearCandidate, closeMap, confirmCandidate, chooseCandidate, elementLabel,
            elementTypeLabel, elements, errorMessage, filteredElements, findUserPosition, gpsWarning,
            handleMapClick, leafletConfig, loading, locating, mapCenter, mapLayers, mapOpen,
            mapPolygons, mapRef, multiple: props.multiple, offline, openMap, searchTerm,
            selectedElement, selectedElements,
            selectedIds, selectedSiteId, selectSite, setValue, sites, userPosition,
            selectedZoneId, selectedElement1Id, selectedMonumentId, zoneOptions, element1Options, monumentOptions,
            useDeviceLocation, listRef, retryLoad,
        };
    },
});
</script>
